import type {
  ReferentialEndoint,
  UseReferentialOptions,
} from "./referential.type";
import type { ApiError } from "@/services/api";

import { useQuery } from "@tanstack/react-query";
import find from "lodash/find";

import { client } from "@/config/client";
import { useGetProfileLocale } from "@/entities/current-user";
import { QUERY_PERSIST_KEY } from "@/services/query";

import {
  DEFAULT_REFERENTIALS_STALE_TIME,
  REFERENTIALS_API_URL,
} from "./referential.constant";

export const useReferential = <Data, Params = unknown>(
  endpoint: ReferentialEndoint,
  options?: UseReferentialOptions<Data, Params>
) => {
  const { persist = true, params, ...queryOptions } = options ?? {};

  const locale = useGetProfileLocale();
  const paramsKey = params ? [params] : [];
  const persistKey = persist ? [QUERY_PERSIST_KEY] : [];
  const queryKey = [
    "referentials",
    endpoint,
    locale,
    ...paramsKey,
    ...persistKey,
  ];

  return useQuery<Data, ApiError>({
    refetchOnWindowFocus: false,
    staleTime: DEFAULT_REFERENTIALS_STALE_TIME,
    ...queryOptions,
    queryKey: queryKey,
    queryFn: async () => {
      const url = `${REFERENTIALS_API_URL}/${endpoint}`;
      const data = params
        ? await client.axios.get(url, { params })
        : await client.axios.get(url);

      if (!data?.data?.data) {
        throw new Error(`Error while fetching referential "${endpoint}"`);
      }

      return data.data.data;
    },
  });
};

export const useDynamicReferential = <Data, Params = unknown>(
  endpoint: ReferentialEndoint,
  options?: UseReferentialOptions<Data[], Params>
) => {
  const { data = [], isLoading } = useReferential<Data[], Params>(
    endpoint,
    options
  );

  const getReferentialData = (value: Partial<Data> = {}) => {
    return find<Data[]>(data, value) as Data | undefined;
  };

  const getReferentialAttr = (value: Partial<Data>, attrName: keyof Data) => {
    const valueData = getReferentialData(value);
    return valueData?.[attrName];
  };

  const getReferentialKeys = (keyName: keyof Data) => {
    return data?.map((item) => item[keyName]) ?? [];
  };

  return {
    data,
    isLoading,
    getReferentialData,
    getReferentialAttr,
    getReferentialKeys,
  };
};
